// Browser-compatible Google Analytics API integration for SEO data analysis
// Uses direct HTTP requests to Google Analytics API instead of Node.js googleapis library

// OAuth 2.0 configuration
const GOOGLE_CLIENT_ID = '997530027176-p7ch11qjb3go0rlggkaaruu7bbs0cpca.apps.googleusercontent.com';
const GOOGLE_CLIENT_SECRET = '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';
const REDIRECT_URI = process.env.NODE_ENV === 'production' 
  ? 'https://lumibears.com/auth/callback'
  : 'http://localhost:3000/auth/callback';

// Required scopes for Google Analytics reporting
const SCOPES = [
  'https://www.googleapis.com/auth/analytics.readonly',
  'https://www.googleapis.com/auth/analytics'
];

// Google API endpoints
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';
const GA_REPORTING_API_URL = 'https://analyticsreporting.googleapis.com/v4/reports:batchGet';
const GA_DATA_API_URL = 'https://analyticsdata.googleapis.com/v1beta';

class GoogleAnalyticsClient {
  constructor() {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
  }

  // Generate authentication URL
  getAuthUrl() {
    const params = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: SCOPES.join(' '),
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent'
    });

    return `${GOOGLE_AUTH_URL}?${params.toString()}`;
  }

  // Exchange authorization code for tokens
  async authenticate(code) {
    try {
      const response = await fetch(GOOGLE_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code: code,
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          redirect_uri: REDIRECT_URI,
          grant_type: 'authorization_code'
        })
      });

      if (!response.ok) {
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const tokens = await response.json();
      
      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      this.tokenExpiry = Date.now() + (tokens.expires_in * 1000);
      
      // Store tokens in localStorage for persistence
      if (typeof window !== 'undefined') {
        localStorage.setItem('ga_tokens', JSON.stringify({
          access_token: this.accessToken,
          refresh_token: this.refreshToken,
          token_expiry: this.tokenExpiry
        }));
      }
      
      return tokens;
    } catch (error) {
      console.error('Authentication failed:', error);
      throw new Error('Failed to authenticate with Google Analytics');
    }
  }

  // Load stored tokens and check validity
  async loadStoredTokens() {
    if (typeof window === 'undefined') return false;
    
    try {
      const storedTokens = localStorage.getItem('ga_tokens');
      if (!storedTokens) return false;
      
      const tokens = JSON.parse(storedTokens);
      
      // Check if tokens are expired
      if (Date.now() >= tokens.token_expiry) {
        if (tokens.refresh_token) {
          await this.refreshAccessToken(tokens.refresh_token);
          return true;
        }
        return false;
      }
      
      this.accessToken = tokens.access_token;
      this.refreshToken = tokens.refresh_token;
      this.tokenExpiry = tokens.token_expiry;
      
      return true;
    } catch (error) {
      console.error('Failed to load stored tokens:', error);
      localStorage.removeItem('ga_tokens');
      return false;
    }
  }

  // Refresh access token using refresh token
  async refreshAccessToken(refreshToken) {
    try {
      const response = await fetch(GOOGLE_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          refresh_token: refreshToken,
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          grant_type: 'refresh_token'
        })
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const tokens = await response.json();
      
      this.accessToken = tokens.access_token;
      this.tokenExpiry = Date.now() + (tokens.expires_in * 1000);
      
      // Update stored tokens
      if (typeof window !== 'undefined') {
        const storedData = {
          access_token: this.accessToken,
          refresh_token: this.refreshToken, // Keep existing refresh token
          token_expiry: this.tokenExpiry
        };
        localStorage.setItem('ga_tokens', JSON.stringify(storedData));
      }
      
      return tokens;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      throw error;
    }
  }

  // Make authenticated request to Google Analytics API
  async makeApiRequest(url, data) {
    if (!this.accessToken) {
      throw new Error('No access token available. Please authenticate first.');
    }

    // Check if token needs refresh
    if (Date.now() >= this.tokenExpiry && this.refreshToken) {
      await this.refreshAccessToken(this.refreshToken);
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Get organic search traffic data using GA4 Data API
  async getOrganicSearchData(propertyId, startDate = '30daysAgo', endDate = 'today') {
    const url = `${GA_DATA_API_URL}/properties/${propertyId}:runReport`;
    
    const requestBody = {
      dateRanges: [{ startDate, endDate }],
      dimensions: [
        { name: 'sessionSource' },
        { name: 'sessionMedium' },
        { name: 'landingPage' }
      ],
      metrics: [
        { name: 'sessions' },
        { name: 'totalUsers' },
        { name: 'screenPageViews' },
        { name: 'bounceRate' },
        { name: 'averageSessionDuration' }
      ],
      dimensionFilter: {
        filter: {
          fieldName: 'sessionMedium',
          stringFilter: {
            matchType: 'EXACT',
            value: 'organic'
          }
        }
      },
      orderBys: [{ metric: { metricName: 'sessions' }, desc: true }],
      limit: 100
    };

    try {
      const response = await this.makeApiRequest(url, requestBody);
      return this.formatAnalyticsResponse(response);
    } catch (error) {
      console.error('Failed to get organic search data:', error);
      throw new Error('Failed to retrieve organic search data');
    }
  }

  // Get top pages performance data
  async getTopPagesData(propertyId, startDate = '30daysAgo', endDate = 'today') {
    const url = `${GA_DATA_API_URL}/properties/${propertyId}:runReport`;
    
    const requestBody = {
      dateRanges: [{ startDate, endDate }],
      dimensions: [
        { name: 'pagePath' },
        { name: 'pageTitle' }
      ],
      metrics: [
        { name: 'screenPageViews' },
        { name: 'totalUsers' },
        { name: 'averageSessionDuration' },
        { name: 'bounceRate' }
      ],
      orderBys: [{ metric: { metricName: 'screenPageViews' }, desc: true }],
      limit: 50
    };

    try {
      const response = await this.makeApiRequest(url, requestBody);
      return this.formatAnalyticsResponse(response);
    } catch (error) {
      console.error('Failed to get top pages data:', error);
      throw new Error('Failed to retrieve top pages data');
    }
  }

  // Format analytics API response for easier consumption
  formatAnalyticsResponse(data) {
    if (!data.rows) return [];

    const dimensionHeaders = data.dimensionHeaders || [];
    const metricHeaders = data.metricHeaders || [];

    return data.rows.map(row => {
      const formattedRow = {};
      
      // Map dimension values
      dimensionHeaders.forEach((header, index) => {
        formattedRow[header.name] = row.dimensionValues?.[index]?.value || '';
      });
      
      // Map metric values
      metricHeaders.forEach((header, index) => {
        formattedRow[header.name] = row.metricValues?.[index]?.value || '0';
      });
      
      return formattedRow;
    });
  }

  // Generate SEO insights from analytics data
  async generateSEOInsights(propertyId) {
    try {
      const [organicData, topPages] = await Promise.all([
        this.getOrganicSearchData(propertyId),
        this.getTopPagesData(propertyId)
      ]);

      const insights = {
        organicTraffic: {
          totalSessions: organicData.reduce((sum, row) => sum + parseInt(row.sessions || 0), 0),
          topLandingPages: organicData.slice(0, 10),
          averageBounceRate: this.calculateAverage(organicData, 'bounceRate')
        },
        topPerformingPages: topPages.slice(0, 10),
        recommendations: this.generateRecommendations(organicData, topPages)
      };

      return insights;
    } catch (error) {
      console.error('Failed to generate SEO insights:', error);
      throw new Error('Failed to generate SEO insights');
    }
  }

  // Calculate average for a metric
  calculateAverage(data, metricName) {
    if (!data.length) return 0;
    const sum = data.reduce((acc, row) => acc + parseFloat(row[metricName] || 0), 0);
    return (sum / data.length).toFixed(2);
  }

  // Generate SEO recommendations based on data
  generateRecommendations(organicData, topPages) {
    const recommendations = [];

    // Check for high bounce rate pages
    const highBouncePages = organicData.filter(page => parseFloat(page.bounceRate || 0) > 70);
    if (highBouncePages.length > 0) {
      recommendations.push({
        type: 'high_bounce_rate',
        priority: 'high',
        message: `${highBouncePages.length} pages have bounce rates above 70%. Consider improving content relevance and page load speed.`,
        pages: highBouncePages.slice(0, 5)
      });
    }

    // Check for pages with good traffic but low engagement
    const lowEngagementPages = organicData.filter(page => 
      parseInt(page.sessions || 0) > 50 && parseFloat(page.averageSessionDuration || 0) < 30
    );
    if (lowEngagementPages.length > 0) {
      recommendations.push({
        type: 'low_engagement',
        priority: 'medium',
        message: `${lowEngagementPages.length} high-traffic pages have low engagement. Consider adding internal links and improving content depth.`,
        pages: lowEngagementPages.slice(0, 5)
      });
    }

    return recommendations;
  }

  // Clear stored authentication
  clearAuth() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('ga_tokens');
    }
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
  }

  // Check if user is currently authenticated
  isAuthenticated() {
    return this.accessToken && Date.now() < this.tokenExpiry;
  }
}

// Export singleton instance
export const googleAnalyticsClient = new GoogleAnalyticsClient();
export default googleAnalyticsClient;